/**
 * Cause List — daily court-side view.
 *
 * Aggregates hearings (auto-created by the eCourts cause-list cron) for a
 * given date, joined with case info. Date-picker default = today.
 *
 *   • Filters: free-text (CNR / Suit / Title) + court + stage + advocate
 *   • Per-row actions: Open case · Add note · Send reminder
 *   • Toolbar: Refresh from eCourts · Print PDF
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Gavel, Calendar as CalendarIcon, RefreshCw, Printer, FileText,
    BellRing, ExternalLink, Loader2, Search, X,
} from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const todayISO = () => new Date().toISOString().slice(0, 10);

/** Convert a UTC ISO datetime → "HH:MM" in IST. Falls back to "" if invalid.
 * Indian courts publish all cause-list times in IST, so we always render IST. */
const istHHMM = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    // Asia/Kolkata is a fixed offset (UTC+5:30, no DST) → safe Intl format
    const f = new Intl.DateTimeFormat("en-IN", {
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata",
    });
    // Some browsers emit "24:00"; normalise to "00:00"
    return f.format(d).replace(/^24:/, "00:");
};

export default function CauseList() {
    const navigate = useNavigate();
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState({ rows: [], facets: { courts: [], stages: [], advocates: [] }, count: 0 });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [actingId, setActingId] = useState("");      // hearing id currently busy
    const [noteFor, setNoteFor] = useState(null);       // { hearing_id, value }

    // filters
    const [q, setQ] = useState("");
    const [court, setCourt] = useState("");
    const [stage, setStage] = useState("");
    const [advocate, setAdvocate] = useState("");

    const load = async (d = date) => {
        setLoading(true);
        try {
            const { data: payload } = await api.get(`/cause-list`, { params: { date: d } });
            setData(payload);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

    const filtersActive = !!(q || court || stage || advocate);

    // Batch detection: many High Courts publish multiple board-times per
    // courtroom (e.g., 10:30 first batch, 12:00 fresh matters). For each court,
    // sort unique times ascending and number them Batch 1, 2, 3… so partners
    // see which slot their matter falls in at a glance. Single-batch courts
    // get no badge (signal-to-noise).
    const batchMap = useMemo(() => {
        const byCourt = new Map();   // court → Set<time>
        for (const r of (data.rows || [])) {
            const ct = r.court || "—";
            const t = r.cause_list_time_ist || istHHMM(r.scheduled_at);
            if (!t) continue;
            if (!byCourt.has(ct)) byCourt.set(ct, new Set());
            byCourt.get(ct).add(t);
        }
        const map = {};   // `${court}|${time}` → { batch: N, total: M }
        for (const [ct, times] of byCourt) {
            const sorted = [...times].sort();
            sorted.forEach((t, i) => {
                map[`${ct}|${t}`] = { batch: i + 1, total: sorted.length };
            });
        }
        return map;
    }, [data.rows]);
    const batchInfo = (r) => {
        const t = r.cause_list_time_ist || istHHMM(r.scheduled_at);
        return batchMap[`${r.court || "—"}|${t}`] || null;
    };

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return (data.rows || []).filter(r => {
            const c = r.case || {};
            if (court && r.court !== court) return false;
            if (stage && c.stage !== stage) return false;
            if (advocate) {
                const adv = [...(c.petitioner_advocates || []), ...(c.respondent_advocates || [])];
                if (!adv.includes(advocate)) return false;
            }
            if (needle) {
                const blob = `${c.cnr || ""} ${c.original_suit_number || ""} ${c.court_case_number || ""} ${c.title || ""} ${r.court || ""} ${r.purpose || ""}`.toLowerCase();
                if (!blob.includes(needle)) return false;
            }
            return true;
        });
    }, [data.rows, q, court, stage, advocate]);

    const refreshFromEcourts = async () => {
        setRefreshing(true);
        try {
            const { data: r } = await api.post(`/cause-list/refresh`, {});
            const created = (r?.result?.hearings_created) ?? 0;
            toast.success(created > 0
                ? `eCourts refresh complete · ${created} hearing${created === 1 ? "" : "s"} added`
                : "eCourts refresh complete · no new hearings today");
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setRefreshing(false); }
    };

    const printPDF = async () => {
        setPrinting(true);
        try {
            const res = await api.get(`/cause-list/print`, { params: { date }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `cause-list-${date}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiError(e)); }
        finally { setPrinting(false); }
    };

    const sendReminder = async (hearingId) => {
        setActingId(hearingId);
        try {
            const { data: r } = await api.post(`/cause-list/${hearingId}/reminder`, {});
            toast.success("Reminder sent to client chat & bell");
            // Optimistic: stamp a note locally
            setData(d => ({ ...d, rows: d.rows.map(x => x.hearing_id === hearingId
                ? { ...x, _reminder_sent: r.message } : x) }));
        } catch (e) { toast.error(apiError(e)); }
        finally { setActingId(""); }
    };

    const saveNote = async () => {
        if (!noteFor) return;
        const { hearing_id, value } = noteFor;
        setActingId(hearing_id);
        try {
            await api.patch(`/hearings/${hearing_id}`, { notes: value });
            toast.success("Note saved");
            setNoteFor(null);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setActingId(""); }
    };

    const clearFilters = () => { setQ(""); setCourt(""); setStage(""); setAdvocate(""); };

    const tableCtrl = useTableControls(filtered, {
        storageKey: "causelist",
        accessors: {
            time: (r) => r.cause_list_time_ist || istHHMM(r.scheduled_at),
            original_case: (r) => r.case?.original_suit_number || r.case?.court_case_number || "",
            title: (r) => r.case?.title || "",
            stage: (r) => r.case?.stage || "",
        },
    });

    return (
        <div className="space-y-6" data-testid="cause-list-page">
            {/* ---- header ---- */}
            <header className="border border-border bg-card px-6 py-5 rounded-sm">
                <div className="flex items-center gap-2 text-primary text-[10px] uppercase tracking-[0.22em] mb-2">
                    <Gavel className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Cause List
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                    Today in court
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Every hearing listed for the chosen date — auto-synced from eCourts, manually
                    refreshable, printable as an A4 cause sheet.
                </p>

                <div className="mt-4 flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Date</label>
                        <div className="relative">
                            <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5}/>
                            <input
                                type="date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                                data-testid="cause-list-date"
                                className="bg-background border border-border focus:border-primary outline-none pl-8 pr-3 py-2 text-xs text-foreground"
                            />
                        </div>
                    </div>
                    <button
                        onClick={() => setDate(todayISO())}
                        data-testid="cause-list-today"
                        disabled={date === todayISO()}
                        className="px-3 py-2 border border-border text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30">
                        Today
                    </button>
                    <button
                        onClick={refreshFromEcourts}
                        disabled={refreshing}
                        data-testid="cause-list-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 text-[10px] uppercase tracking-[0.18em]">
                        {refreshing ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3" strokeWidth={1.5}/>}
                        Refresh from eCourts
                    </button>
                    <button
                        onClick={printPDF}
                        disabled={printing || filtered.length === 0}
                        data-testid="cause-list-print"
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-border text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30">
                        {printing ? <Loader2 className="w-3 h-3 animate-spin"/> : <Printer className="w-3 h-3" strokeWidth={1.5}/>}
                        Print PDF
                    </button>
                </div>
            </header>

            {/* ---- filter bar ---- */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,180px,180px,auto] gap-2 items-center"
                 data-testid="cause-list-filters">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5}/>
                    <input
                        type="text"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search CNR · Original Case # · Title · Court · Purpose"
                        data-testid="cause-list-search"
                        className="w-full bg-background border border-border focus:border-primary outline-none pl-8 pr-8 py-2 text-xs text-foreground placeholder:text-muted-foreground"/>
                    {q && (
                        <button onClick={() => setQ("")} aria-label="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        </button>
                    )}
                </div>
                <select value={court} onChange={e => setCourt(e.target.value)} data-testid="cause-list-filter-court"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                    <option value="">All courts</option>
                    {data.facets.courts.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={stage} onChange={e => setStage(e.target.value)} data-testid="cause-list-filter-stage"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                    <option value="">All stages</option>
                    {data.facets.stages.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={advocate} onChange={e => setAdvocate(e.target.value)} data-testid="cause-list-filter-advocate"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                    <option value="">All advocates</option>
                    {data.facets.advocates.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <button onClick={clearFilters} disabled={!filtersActive}
                    data-testid="cause-list-filter-clear"
                    className="px-3 py-2 border border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30">
                    Clear
                </button>
            </div>

            {/* ---- counter ---- */}
            <div className="text-xs text-muted-foreground" data-testid="cause-list-count">
                {loading ? "Loading…" :
                    filtersActive
                        ? `${filtered.length} of ${data.count} matter${data.count === 1 ? "" : "s"}`
                        : `${data.count} matter${data.count === 1 ? "" : "s"} listed on ${date}`}
            </div>

            {/* ---- table ---- */}
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <SortableTh field="time" ctrl={tableCtrl} className="text-left p-2 w-20">Time</SortableTh>
                            <SortableTh field="original_case" ctrl={tableCtrl} className="text-left p-2">Original Case # / Court #</SortableTh>
                            <SortableTh field="title" ctrl={tableCtrl} className="text-left p-2">Title</SortableTh>
                            <SortableTh field="court" ctrl={tableCtrl} className="text-left p-2">Court</SortableTh>
                            <SortableTh field="stage" ctrl={tableCtrl} className="text-left p-2">Stage</SortableTh>
                            <SortableTh field="purpose" ctrl={tableCtrl} className="text-left p-2">Purpose</SortableTh>
                            <SortableTh field="notes" ctrl={tableCtrl} className="text-left p-2">Notes</SortableTh>
                            <th className="text-right p-2 w-44">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && tableCtrl.rows.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                                {filtersActive ? "No matters match the current filters." : "Nothing listed on this date."}
                            </td></tr>
                        )}
                        {tableCtrl.rows.map(r => {
                            const c = r.case || {};
                            const t = r.cause_list_time_ist || istHHMM(r.scheduled_at);
                            const bi = batchInfo(r);
                            const busy = actingId === r.hearing_id;
                            return (
                                <tr key={r.hearing_id} className="border-t border-border hover:bg-muted/50" data-testid={`cause-list-row-${r.hearing_id}`}>
                                    <td className="p-2 font-mono">
                                        <div>{t || "—"}</div>
                                        {bi && bi.total > 1 && (
                                            <span
                                                title={`Batch ${bi.batch} of ${bi.total} in ${r.court || "this court"} (sorted by board time)`}
                                                data-testid={`cause-list-batch-${r.hearing_id}`}
                                                className="inline-block mt-1 px-1.5 py-[1px] border border-amber-500/40 bg-amber-500/[0.08] text-amber-300 text-[9px] uppercase tracking-[0.12em] font-mono leading-tight">
                                                Batch {bi.batch}/{bi.total}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-2 font-mono text-[10px]">
                                        {c.original_suit_number && <div className="text-primary">{c.original_suit_number}</div>}
                                        {c.court_case_number && <div className="text-muted-foreground">CIS {c.court_case_number}</div>}
                                        {!c.original_suit_number && !c.court_case_number && <span className="text-[#5A5A5A]">—</span>}
                                    </td>
                                    <td className="p-2">{c.title || <span className="text-[#5A5A5A]">—</span>}</td>
                                    <td className="p-2 text-muted-foreground">{r.court || <span className="text-[#5A5A5A]">—</span>}</td>
                                    <td className="p-2 text-muted-foreground">{c.stage || <span className="text-[#5A5A5A]">—</span>}</td>
                                    <td className="p-2 text-muted-foreground">{r.purpose || <span className="text-[#5A5A5A]">—</span>}</td>
                                    <td className="p-2 text-muted-foreground max-w-[200px] truncate" title={r.notes || ""}>
                                        {r.notes || <span className="text-[#5A5A5A]">—</span>}
                                    </td>
                                    <td className="p-2 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            {c.id && (
                                                <button onClick={() => navigate(`/app/cases/${c.id}`)}
                                                    title="Open case"
                                                    data-testid={`cause-list-open-${r.hearing_id}`}
                                                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-background">
                                                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                                </button>
                                            )}
                                            <button onClick={() => setNoteFor({ hearing_id: r.hearing_id, value: r.notes || "" })}
                                                title="Add note"
                                                data-testid={`cause-list-note-${r.hearing_id}`}
                                                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-background">
                                                <FileText className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                            </button>
                                            <button onClick={() => sendReminder(r.hearing_id)}
                                                disabled={busy || !c.id}
                                                title="Send client reminder"
                                                data-testid={`cause-list-reminder-${r.hearing_id}`}
                                                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-background disabled:opacity-30">
                                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <BellRing className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ---- note modal ---- */}
            {noteFor && (
                <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4"
                     onClick={() => setNoteFor(null)}>
                    <div className="bg-card border border-border w-full max-w-lg p-5"
                         onClick={e => e.stopPropagation()}
                         data-testid="cause-list-note-modal">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary mb-2">Hearing note</div>
                        <h3 className="text-base text-foreground mb-4">Add or edit note</h3>
                        <textarea
                            value={noteFor.value}
                            onChange={e => setNoteFor({ ...noteFor, value: e.target.value })}
                            rows={5}
                            data-testid="cause-list-note-input"
                            placeholder="e.g. adjourned to 22-Jun-2026 · evidence partly recorded"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground"/>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setNoteFor(null)}
                                className="px-3 py-1.5 border border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                                Cancel
                            </button>
                            <button onClick={saveNote}
                                disabled={actingId === noteFor.hearing_id}
                                data-testid="cause-list-note-save"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium disabled:opacity-50">
                                {actingId === noteFor.hearing_id ? <Loader2 className="w-3 h-3 animate-spin"/> : null}
                                Save note
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
